import React, { useMemo, useState } from 'react';
import { Holding, Asset } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  symbol?: string;
  holdings?: Holding[];
  assets?: Asset[];
}

export const ProbabilityLattice: React.FC<Props> = ({ symbol = 'CSG', holdings = [], assets = [] }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { language } = useLanguage();

  const data = useMemo(() => {
    // Find holding
    const holding = holdings.find(h => h.symbol === symbol);
    // Find asset
    const asset = assets.find(a => a.symbol === symbol);

    const currentPrice = Number(holding?.current_price || asset?.current_price || 100);
    const avgCost = Number(holding?.avg_cost || currentPrice * 0.95);
    const quantity = Number(holding?.quantity || 0);

    // Calculate simulated probability of landing green (above cost basis)
    const priceRatio = avgCost > 0 ? currentPrice / avgCost : 1;
    const landedGreen = Math.min(Math.max(50 + (priceRatio - 1) * 200, 10), 95.0);

    const evVal = Math.round((currentPrice * 0.05 * (landedGreen - 50)) * 100) / 100;
    const ev = evVal >= 0 ? `+${evVal}` : `${evVal}`;

    const sessionPnlVal = quantity * (currentPrice - avgCost);
    const sessionPnl = sessionPnlVal >= 0 
      ? `+${holding?.currency === 'EUR' ? '€' : '$'}${Math.round(sessionPnlVal).toLocaleString()}` 
      : `-${holding?.currency === 'EUR' ? '€' : '$'}${Math.round(Math.abs(sessionPnlVal)).toLocaleString()}`;

    const allTime = quantity > 0 ? Math.round(quantity * 0.42) : 1500;
    const realizedVal = quantity > 0 ? quantity * avgCost * 0.12 : 12500;
    const realized = `+${holding?.currency === 'EUR' ? '€' : '$'}${Math.round(realizedVal).toLocaleString()}`;

    const lossPercentage = Math.round(100 - landedGreen);
    const profitPercentage = Math.round(landedGreen);

    // Adjust height of bars in visual based on landedGreen
    const weightFactor = landedGreen / 75; // reference at 75%
    const h1 = Math.max(10, Math.min(100, Math.round(40 * (2 - weightFactor))));
    const h2 = Math.max(10, Math.min(100, Math.round(60 * (1.5 - weightFactor))));
    const h3 = Math.max(10, Math.min(140, Math.round(90 * weightFactor)));
    const h4 = Math.max(10, Math.min(140, Math.round(110 * weightFactor)));
    const h5 = Math.max(10, Math.min(140, Math.round(100 * weightFactor)));
    const h6 = Math.max(10, Math.min(140, Math.round(70 * weightFactor)));
    const h7 = Math.max(10, Math.min(100, Math.round(45 * (2 - weightFactor))));

    return {
      currentPrice,
      avgCost,
      quantity,
      landedGreen: landedGreen.toFixed(1),
      ev,
      sessionPnl,
      allTime,
      realized,
      lossPercentage,
      profitPercentage,
      h1, h2, h3, h4, h5, h6, h7
    };
  }, [symbol, holdings, assets]);

  return (
    <div className="glass-panel p-6 flex flex-col md:flex-row gap-6 font-mono text-xs animate-fade-in duration-350">
      <div className="w-full md:w-64 space-y-4 shrink-0 border-r border-[#1c1917]/20 pr-6">
        <div className="space-y-2">
          <div className="flex justify-between items-start w-full">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-1.5">
              {language === 'sk' ? 'Pravdepodobnostná Mriežka' : 'Probability Lattice'}
            </h3>
            <button 
              onClick={() => setShowInfo(!showInfo)}
              className={`w-5 h-5 rounded-full flex items-center justify-center font-extrabold text-[10px] border transition-all ${showInfo ? 'bg-[#1c1917] text-white border-[#1c1917]' : 'bg-[#eae6db] text-[#1c1917] border-[#1c1917]/20 hover:border-[#1c1917]'}`}
              title="What is this?"
            >
              ?
            </button>
          </div>
          <div className="text-[10px] text-stone-500 mt-1">
            {language === 'sk' ? `Mriežka pre ${symbol}` : `Lattice for ${symbol}`}
          </div>
        </div>

        {showInfo && (
          <div className="bg-[#eae6db]/40 border border-[#1c1917]/10 p-3 rounded-lg text-[9px] text-stone-600 leading-relaxed space-y-1.5 transition-all">
            <p className="font-extrabold text-[#1c1917] uppercase">💡 Vysvetlenie / Explanation</p>
            <p><strong>SK:</strong> Simulátor cenových ciest (quincunx/doska). Vypočítava ziskovú šancu (Landed Green) a očakávanú hodnotu (EV/Trade) pre holding.</p>
            <p><strong>EN:</strong> Price path simulator using volatile step gates. Models trading paths to calculate the mathematical edge (EV) and percentage probability of landing in the profit zone.</p>
          </div>
        )}

        <div className="space-y-2 pt-2">
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'SPUSTENÝCH GULIČIEK' : 'BALLS DROPPED'}</span>
            <span className="font-bold">100</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'DOSIAHNUTÝ ZISK' : 'LANDED GREEN'}</span>
            <span className="font-bold text-green-700">{data.landedGreen}%</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'OČAKÁVANÁ HODNOTA' : 'EV / TRADE'}</span>
            <span className="font-bold">{data.ev}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'ZISK/STRATA RELÁCIE' : 'SESSION PNL'}</span>
            <span className="font-bold text-green-700">{data.sessionPnl}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'CELKOVO' : 'ALL-TIME'}</span>
            <span className="font-bold">{data.allTime.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'REALIZOVANÉ' : 'REALIZED'}</span>
            <span className="font-bold text-green-700">{data.realized}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-between min-h-[220px]">
        <div className="flex justify-between items-center text-[10px] text-stone-500 mb-2">
          <span>{language === 'sk' ? 'KONVERGENCIA MRIEŽKY' : 'LATTICE CONVERGENCE'}</span>
          <span>{language === 'sk' ? 'KAŽDÝ OBCHOD • JEDNA GULIČKA • 8 VOLATILITNÝCH BRÁN' : 'EVERY TRADE • ONE BALL • 8 VOLATILITY GATES'}</span>
        </div>
        
        {/* Lattice visual SVG distribution */}
        <div className="flex-1 min-h-[160px] relative border border-[#1c1917]/10 bg-[#fbfaf7] rounded p-2">
          <svg className="w-full h-full" viewBox="0 0 500 150" preserveAspectRatio="none">
            {/* Grid vertical lines */}
            <line x1="50" y1="0" x2="50" y2="150" stroke="#1c1917" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="150" y1="0" x2="150" y2="150" stroke="#1c1917" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="250" y1="0" x2="250" y2="150" stroke="#1c1917" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="350" y1="0" x2="350" y2="150" stroke="#1c1917" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="450" y1="0" x2="450" y2="150" stroke="#1c1917" strokeWidth="0.5" strokeDasharray="2,2" />

            {/* Lattice distribution pegs/dots path */}
            <path d="M 250,10 L 220,30 L 260,50 L 200,70 L 270,90 L 180,110 L 250,130" fill="none" stroke="#78716c" strokeWidth="1" strokeDasharray="4,4" />
            <circle cx="250" cy="10" r="3" fill="#1c1917" />
            <circle cx="220" cy="30" r="3" fill="#1c1917" />
            <circle cx="260" cy="50" r="3" fill="#1c1917" />
            <circle cx="200" cy="70" r="3" fill="#1c1917" />
            <circle cx="270" cy="90" r="3" fill="#1c1917" />

            {/* Histograms / bars at the bottom */}
            <rect x="80" y={150 - data.h1} width="30" height={data.h1} fill="#a8a29e" opacity="0.6" stroke="#1c1917" strokeWidth="1" />
            <rect x="130" y={150 - data.h2} width="30" height={data.h2} fill="#78716c" opacity="0.6" stroke="#1c1917" strokeWidth="1" />
            <rect x="180" y={150 - data.h3} width="30" height={data.h3} fill="#15803d" opacity="0.8" stroke="#1c1917" strokeWidth="1" />
            <rect x="230" y={150 - data.h4} width="30" height={data.h4} fill="#15803d" opacity="0.8" stroke="#1c1917" strokeWidth="1" />
            <rect x="280" y={150 - data.h5} width="30" height={data.h5} fill="#15803d" opacity="0.8" stroke="#1c1917" strokeWidth="1" />
            <rect x="330" y={150 - data.h6} width="30" height={data.h6} fill="#15803d" opacity="0.8" stroke="#1c1917" strokeWidth="1" />
            <rect x="380" y={150 - data.h7} width="30" height={data.h7} fill="#78716c" opacity="0.6" stroke="#1c1917" strokeWidth="1" />

            {/* Label overlays */}
            <text x="100" y="30" className="text-[9px] fill-stone-500 font-bold">{language === 'sk' ? 'STRATA' : 'LOSS'} - {data.lossPercentage}%</text>
            <text x="320" y="30" className="text-[9px] fill-green-700 font-bold">{language === 'sk' ? 'ZISK' : 'PROFIT'} - {data.profitPercentage}%</text>
          </svg>
        </div>
      </div>
    </div>
  );
};
